import * as React from "react";
import UploadIcon from "./uploadIconSvg";
import { injectIntl, IntlShape } from "react-intl";
import { SupportItemTypes } from "modules/support";
import { useSetSnackbarMessages } from "modules/common/hooks/useSetSnackbarMessages";
import { AttachmentType } from "modules/common/models";
import { DocumentUploadType, FileUploadData, FileUploadReturn } from "../models";

import "./uploader.sass";

interface IUploaderProps {
    uploading: boolean;
    maxUploadable: number;
    setUploading: (uploading: boolean) => void;
    completed?: (imageId: string) => void;
    upload?: (
        file: File,
        onProgress: (percentCompleted: number) => void, 
        onLoad: (e: ProgressEvent<EventTarget>, xhr: XMLHttpRequest) => void, 
        onError: (e: ProgressEvent<EventTarget>, xhr: XMLHttpRequest) => void, 
        onAbort: (e: ProgressEvent<EventTarget>, xhr: XMLHttpRequest) => void,
    ) => Promise<FileUploadReturn | null>;
    uploadAll?: (files: FileUploadData[]) => Promise<FileUploadReturn[] | null>;
    intl: IntlShape;
    onAllComplete?: () => void;
    documentType?: string;
    onDocumentComplete?: (docId: string, fileName: string, checkSum?: string, file?: File) => void;
    attachmentType: AttachmentType;
    onSupportDocumentComplete?: (docId: string, fileName: string) => void
    acceptedFiles?: string;
    uploaderMessage?: string;
    exceededSizeLimit?: (totalFileSize: number) => boolean;
}

export interface IUploadStatus {
    fileProgress: Record<string, number>;
    error: string | null;
    aborted: boolean;
}

const MAX_UPLOADS = 20;
const ACCEPTED_FILE_TYPES_STR = "image/jpg,image/jpeg,image/png,image/bmp,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip,application/zip,application/x-zip-compressed";

const Uploader: React.FC<IUploaderProps> = ({
    maxUploadable,
    uploading,
    setUploading,
    completed,
    upload,
    uploadAll,
    intl,
    onAllComplete,
    documentType,
    onDocumentComplete,
    attachmentType,
    onSupportDocumentComplete,
    acceptedFiles,
    uploaderMessage,
    exceededSizeLimit
}) => {
    const [isDragging, setIsDragging] = React.useState(false);
    const [uploadStatus, setUploadStatus] = React.useState<IUploadStatus>({
        fileProgress: {},
        error: null,
        aborted: false,
    });
    const [totalToUpload, setTotalToUpload] = React.useState(0);
    const [totalUploaded, setTotalUploaded] = React.useState(0);
    const { setErrorMessage } = useSetSnackbarMessages();

    const onDragging = (isDragging: boolean, callback?: () => void) => (e: React.DragEvent | React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (typeof callback !== "function") callback = () => {};

        if (exceededSizeLimit) {
            exceededSizeLimit(Number.MIN_VALUE);
        }

        setIsDragging(isDragging);
        callback();

        return false;
    };

    const resetUploadStatus = () => {
        setUploadStatus(prev => ({...prev, aborted: false, error: null}));
    };

    const onUploadProgress = (percentCompleted: number, fileName: string) => {
        setUploadStatus(prev => ({...prev, fileProgress: {...prev.fileProgress, [fileName]: percentCompleted}}));
    };

    const onUploadComplete = (e: ProgressEvent<EventTarget>) => {};

    const onUploadError = (e: ProgressEvent<EventTarget>, xhr: XMLHttpRequest) => {
        setUploadStatus((prev) => ({...prev, error: xhr.responseText }));
    };

    const onUploadAborted = (e: ProgressEvent<EventTarget>) => {
        setUploadStatus((prev) => ({...prev, aborted: true }));
    };

    const completedAll = () => {
        setUploading(false);
        if (onAllComplete)
            onAllComplete();
    };

    const onUploadCompleteCallback = (fileUploadResponse: FileUploadReturn | null) => {
        if (fileUploadResponse === null) 
            return;

        switch (fileUploadResponse.documentUploadType) {
            case DocumentUploadType.SupportDocument:
                if (onSupportDocumentComplete)
                    onSupportDocumentComplete(fileUploadResponse.id, fileUploadResponse.fileName!);
                break;
            case DocumentUploadType.PostDocument:
                if (onDocumentComplete)
                    onDocumentComplete(fileUploadResponse.id, fileUploadResponse.fileName!);
                break;
            case DocumentUploadType.Image:
                if (completed)
                    completed(fileUploadResponse.id);
                break;
        }
    }
    
    const onUpload = (files: FileUploadData[]) => {
        if (uploadAll !== undefined) {
            uploadAll(files)
            .then((responses) => {
                if (!responses) 
                    return;

                setTotalToUpload(Math.min(files.length, responses.length));
                setTotalUploaded(responses.length);

                responses.forEach(onUploadCompleteCallback);
            })
            .catch(_ => {
                setErrorMessage(
                    intl.formatMessage(
                        { defaultMessage: "Failed to upload files"}
                    )
                );
            })
            .finally(() => completedAll());
        }
        else if (upload !== undefined) {
            const fileData = files[0];

            if (!fileData || !fileData.file) {
                setErrorMessage(intl.formatMessage({ id: "posts.noFilesFound" }));
                return;
            }

            const file = fileData.file;

            upload(
                file,
                (p) => onUploadProgress(p, file.name),
                onUploadComplete,
                onUploadError,
                onUploadAborted
            )
            .then(response => {
                if(!response) {
                    setTotalToUpload(prev => prev - 1);
                }
                else {
                    setTotalUploaded(prev => prev + 1);
                    onUploadCompleteCallback({
                        ...response, 
                        documentUploadType: fileData.documentUploadType
                    });
                }
            })
            .catch(_ => {
                setErrorMessage(
                    intl.formatMessage(
                        { defaultMessage: "Failed to upload file '{file}'"},
                        { file: file.name }
                    )
                );
            })
            .finally(() => completedAll());
        }
    }

    const onFileInputChanged = (e: React.FormEvent<HTMLInputElement>) => {
        const files = (e.target as HTMLInputElement).files;
        onDragging(
            false, 
            () => uploadFiles(files!)
        )(e);
    };

    const uploadFiles = (files: FileList) => {
        if (files.length < 1) {
            setErrorMessage(intl.formatMessage({ id: "posts.noFilesFound" }));
        } 
        else if (files.length > MAX_UPLOADS || files.length > maxUploadable) {
            let max = Math.min(MAX_UPLOADS, maxUploadable || MAX_UPLOADS);
            setErrorMessage(`No more than ${max} file(s) can be uploaded`);
        }
        else {
            const allowedFiles = Array.from(files).filter(isFileTypeAllowed);
            const disallowedFiles = Array.from(files).filter((f) => !isFileTypeAllowed(f));

            if (disallowedFiles.length) {
                const exts = Array.from(new Set(disallowedFiles.map(getFileExt))).join(", ");
                setErrorMessage(
                    intl.formatMessage(
                        { id: "unrecognizedFileType", defaultMessage: "File type not recognized: {fileType}" },
                        { fileType: exts }
                    )
                );
            }

            if (exceededSizeLimit) {
                const totalFilesSize = Array.from(allowedFiles).reduce((acc, file) => acc + file.size, 0);
    
                const exceededLimit = exceededSizeLimit(totalFilesSize);

                if (exceededLimit)
                    return;
            }

            setUploading(true);
            setTotalToUpload(allowedFiles.length);
            setTotalUploaded(0);
            setUploadStatus(prev => ({...prev, fileProgress: Array.from(allowedFiles).reduce((acc, val) => ({ [val.name]: 0 }), {})}));

            let documentUploads: FileUploadData[] = [];
            resetUploadStatus();

            for (let i = 0; i < allowedFiles.length; i++) {
                let file = allowedFiles[i];

                let ext = getFileExt(file);

                if (filenameContainsInvalidCharacters(file.name)) {
                    setErrorMessage(`File name must not contain any special characters. Please reupload without the following characters:  # / ? % * : | " < >`)
                    break;
                }

                if (
                    documentType === SupportItemTypes.faq ||
                    documentType === SupportItemTypes.knownIssue ||
                    documentType === SupportItemTypes.releaseNote ||
                    documentType === SupportItemTypes.releasePackage ||
                    documentType === SupportItemTypes.training
                ) {

                    if (!isFileUnderMbSize(file, 20)) {
                        setErrorMessage(intl.formatMessage({id: "posts.documentSizeLimit"}, {size: '20MB'}));
                        break;
                    }

                    if (isDocumentFileType(file)) {
                        documentUploads.push({
                            file, 
                            documentUploadType: DocumentUploadType.SupportDocument,
                            onUploadProgress: (p) => onUploadProgress(p, file.name),
                            onUploadComplete: onUploadComplete,
                            onUploadError: onUploadError,
                            onUploadAbort: onUploadAborted
                        });
                    } else {
                        setErrorMessage(
                            intl.formatMessage(
                                { id: "unrecognizedFileType", defaultMessage: "File type not recognized: {fileType}" },
                                { fileType: ext }
                            )
                        );
                    }
                }
                /* Deals with all other cases, such as posts */
                else {
                    /* Media specific widget in posts */
                    if (attachmentType === AttachmentType.Media) {
                        if (!isFileUnderMbSize(file, 6)) {
                            setErrorMessage(intl.formatMessage({ id: "posts.imageSizeLimit" }, { size: "6MB" }));
                            break;
                        }
                        if (isImageType(file)) {
                            documentUploads.push({
                                file,
                                documentUploadType: DocumentUploadType.Image,
                                onUploadProgress: (p) => onUploadProgress(p, file.name),
                                onUploadComplete: onUploadComplete,
                                onUploadError: onUploadError,
                                onUploadAbort: onUploadAborted
                            });
                        }
                        else {
                            setErrorMessage(
                                intl.formatMessage(
                                    { id: "unrecognizedFileType", defaultMessage: "File type not recognized: {fileType}" },
                                    { fileType: ext }
                                )
                            );
                        }
                    /* Document specific widget in posts */
                    } else if (attachmentType === AttachmentType.Files) {
                        if (!isFileUnderMbSize(file, 20)) {
                            setErrorMessage(intl.formatMessage({ id: "posts.documentSizeLimit" }, { size: "20MB" }));
                            break;
                        }

                        if (isDocumentFileType(file)) {
                            documentUploads.push({
                                file,
                                documentUploadType: DocumentUploadType.PostDocument,
                                onUploadProgress: (p) => onUploadProgress(p, file.name),
                                onUploadComplete: onUploadComplete,
                                onUploadError: onUploadError,
                                onUploadAbort: onUploadAborted
                            });
                        } else {
                            setErrorMessage(
                                intl.formatMessage(
                                    { id: "unrecognizedFileType", defaultMessage: "File type not recognized: {fileType}" },
                                    { fileType: ext }
                                )
                            );
                        }
                    }
                }
            }

            onUpload(documentUploads);
        }
    };

    // https://stackoverflow.com/a/12900504 for the black magic below
    const getFileExt = (file: File) => file.name.slice((file.name.lastIndexOf(".") - 1 >>> 0) + 2) || "unknown";

    const isFileTypeAllowed = (file: File) => isDocumentFileType(file) || isImageType(file);

    const onFileDropped = (e: React.DragEvent<HTMLDivElement>) => {
        const files = e.dataTransfer.files;
        onDragging(
            false, 
            () => uploadFiles(files)
        )(e);
    };

    const renderUploadForm = (): JSX.Element => {
        let draggingProps = {
            onDrag: onDragging(true),
            onDragStart: onDragging(true),
            onDragEnd: onDragging(false),
            onDragOver: onDragging(true),
            onDragEnter: onDragging(true),
            onDragLeave: onDragging(false),
            onDrop: onFileDropped,
        };

        return <div className={"uploader" + (isDragging ? " is-dragging" : "")} {...draggingProps}>
            <div className="centered-content">
                <UploadIcon color="#ccc" width="75px" height="75px" />
                <div>
                    <input
                        className="original-input"
                        id="choose-file"
                        multiple
                        name="choose-file"
                        type="file"
                        accept={acceptedFiles ?? ACCEPTED_FILE_TYPES_STR}
                        disabled={!Math.min(MAX_UPLOADS, isNaN(maxUploadable) ? Infinity : maxUploadable)}
                        onChange={onFileInputChanged}
                    />
                    <div className="large">
                        <label className="choose-file-button" htmlFor="choose-file">
                            {
                                uploaderMessage || 
                                `Choose or drag ${acceptedFiles === "application/pdf" ? "PDF" : "PNG or JPEG"} files here (up to ${MAX_UPLOADS})`
                            }
                        </label>
                    </div>
                </div>
            </div>
        </div>;
    }

    const renderUploadingProgress = (): JSX.Element => 
        <div className="uploader">
            <div className="centered-content">
                <div>
                    <p className="large">{intl.formatMessage({ id: "posts.uploading", defaultMessage: "Uploading" })}...</p>
                    {
                        Object.keys(uploadStatus.fileProgress).map((k) => {
                            return (
                                <div className="progress-bar-wrapper" key={`file-progress-${k}`}>
                                    <label>{k}</label>
                                    <div className="progress">
                                        <div className="bar" style={{ width: `${uploadStatus.fileProgress[k]}%` }}></div>
                                    </div>
                                </div>
                            );
                        })
                    }
                    <p>
                        {totalUploaded}/{totalToUpload} Uploaded
                    </p>
                </div>
            </div>
        </div>;

    const renderUploadingError = (): JSX.Element => 
        <div className="uploader">
            <div className="centered-content">
                <div>
                    <p className="large">{intl.formatMessage({ id: "posts.failedUpload" })}</p>
                    <p>
                        <button onClick={resetUploadStatus}>{intl.formatMessage({ id: "common.tryAgain" })}</button>
                    </p>
                </div>
            </div>
        </div>;

    const renderUploadingAborted = (): JSX.Element => 
        <div className="uploader">
            <div className="centered-content">
                <div>
                    <p className="large">{intl.formatMessage({ id: "posts.uploadCancelled" })}</p>
                    <p>
                        <button className="btn" onClick={resetUploadStatus}>
                            {intl.formatMessage({ id: "posts.uploadAgain" })}
                        </button>
                    </p>
                </div>
            </div>
        </div>;

    if (uploadStatus.aborted) 
        return renderUploadingAborted();
    else if (uploadStatus.error) 
        return renderUploadingError();
    else if (uploading) 
        return renderUploadingProgress();

    return renderUploadForm();
}

export const invalidCharactersArray = ["#", "\\", "?", ":", "|", "\"", "&", ";", "@", "+", "="];

// this array will match a string that contains any invalid filename characters in invalidCharactersArray
export const invalidCharactersRegex = new RegExp(`[${invalidCharactersArray.join()}]`);

export const filenameContainsInvalidCharacters = (filename: string) => {
    return invalidCharactersRegex.test(filename)
}

export const isDocumentFileType = (file: File) => {
    return /^application\/(vnd.openxmlformats-officedocument.wordprocessingml.document|vnd.openxmlformats-officedocument.presentationml.presentation|vnd.openxmlformats-officedocument.spreadsheetml.sheet|pdf)/.test(file.type) ||
           /^image\/(jpg|jpeg|png)/.test(file.type) ||
           /^text\/plain/.test(file.type);
};

export const isImageType = (file: File) => {
    return /^image\/(jpg|jpeg|png)/.test(file.type);
};

export const isFileUnderMbSize = (file: File, mbSize: number = 6) => {
    return file.size < 1024 * mbSize * 1024;
}

export const fileUnderAllowedDimensions = (file: File, maxWidth: number = 10000, maxHeight: number = 10000): Promise<boolean> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => {
            if (img.width <= maxWidth && img.height <= maxHeight) {
                resolve(true);
            } else {
                resolve(false);
            }
        };
        img.onerror = () => {
            reject(new Error("Failed to load image."));
        };
    });
};

export default injectIntl(Uploader);
